import {
    AfterViewInit,
    Component,
    computed,
    EventEmitter,
    input,
    InputSignal,
    Output,
    signal,
    Signal,
    TemplateRef,
    ViewChild,
    WritableSignal,
} from '@angular/core';
import { Invoice } from '../../invoice.model';
import { CommonModule } from '@angular/common';
import { AnchorRouteDirective } from '../../../../UI-Lib/Directives';
import { SumPipe } from '../../../../UI-Lib/Pipes/Sum/sum.pipe';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SortByPipe } from '../../../../UI-Lib/Pipes';
import { Constants } from '../../../../constants.new';
import { Subject } from 'rxjs';
import { InvoiceDetailsService } from '../invoiceDetails.service';
import { HelpTooltipDirective } from '../../../../UI-Lib/Help-Tooltip';

interface InvoiceColumn {
    field: string;
    header: string;
    template: TemplateRef<any>;
    helpContentId?: string;
    alignRight?: boolean;
    hasDollarSign?: boolean;
    hasValues?: (taxSavings: Core.AppealSavingsCalculationDetails[]) => boolean;
}

type RevisionNames = {
    [appealId: number]: {
        hasSavingsOverride: boolean;
        savingsOverride: number;
    };
};

@Component({
    selector: 'tax-savings-details',
    templateUrl: './taxSavingsDetails.component.html',
    styleUrls: ['./taxSavingsDetails.component.scss'],
    imports: [CommonModule, AnchorRouteDirective, SumPipe, SortByPipe, ReactiveFormsModule, HelpTooltipDirective],
})
export class TaxSavingsDetailsComponent implements AfterViewInit {
    constructor(
        public constants: Constants,
        private readonly _invoiceDetailsService: InvoiceDetailsService,
    ) {}

    @Output() otherParcelsChanged: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('percentCellTemplate') percentCellTemplate: TemplateRef<any>;
    @ViewChild('numberCellTemplate') numberCellTemplate: TemplateRef<any>;
    @ViewChild('textCellTemplate') textCellTemplate: TemplateRef<any>;

    currentInvoice: InputSignal<Invoice> = input();
    invoiceForm: InputSignal<FormGroup> = input();

    columns: Signal<InvoiceColumn[]> = computed(() => {
        const invoice = this.currentInvoice();
        if (invoice?.taxSavings?.length) {
            const columns = this._allColumns().filter((col) => {
                if (col.hasValues) {
                    return col.hasValues(invoice.taxSavings);
                }
                return true;
            });
            if (invoice.taxSavings.some((x) => x.hasAnyCap)) {
                // rename cap to cap name
                const capColumn = columns.find((c) => c.field === 'cap');
                if (capColumn) {
                    const capNames = new Set(invoice.taxSavings.filter((x) => x.capName).map((x) => x.capName));
                    const [firstCapName] = capNames;
                    capColumn.header = capNames.size > 1 ? 'Caps MV' : `${firstCapName} MV`;
                }
            }

            this.hasAnySavingsOverride = false;
            this.appealRef = invoice.taxSavings.reduce((acc, x) => {
                const appeal = invoice.appeals.find((y) => y.appealID === x.appealId);
                if (appeal) {
                    this.hasAnySavingsOverride = this.hasAnySavingsOverride || appeal.savingsOverridden;
                    acc[x.appealId] = {
                        hasSavingsOverride: appeal.savingsOverridden,
                        savingsOverride: appeal.savings,
                    };
                }
                return acc;
            }, {});
            return columns;
        } else {
            return this._allColumns();
        }
    });

    totalSavings: Signal<number> = computed(() => {
        const invoice = this.currentInvoice();
        if (invoice?.taxSavings?.length) {
            return invoice.taxSavings.reduce((acc, x) => {
                if (this.appealRef[x.appealId]?.hasSavingsOverride) {
                    return acc + (this.appealRef[x.appealId].savingsOverride || 0);
                }
                return acc + (x.totalSavings || 0);
            }, 0);
        }
        return 0;
    });

    appealRef: RevisionNames = {};
    hasAnySavingsOverride: boolean;
    includeOtherParcels: boolean;

    isMultiParcelInvoice: Signal<boolean> = computed(() => {
        return this.currentInvoice()?.taxSavings?.length > 1;
    });

    private _allColumns: WritableSignal<InvoiceColumn[]> = signal([]);
    private _destroy$ = new Subject<void>();

    parcelName = (summary: Core.AppealSavingsCalculationDetails) => {
        return summary.parcelSavingsCalculations[0].parcelName;
    };

    ngAfterViewInit(): void {
        this._allColumns.set([
            {
                field: 'taxableValueName',
                header: 'Val Type',
                helpContentId: 'invoice-details.taxableValue',
                template: this.textCellTemplate,
                hasValues: (taxSavings) => {
                    return taxSavings.some((x) => {
                        return x.parcelSavingsCalculations.some((y) => {
                            return (
                                y.outcomeRevisionCalculations.some((z) => z.taxableValueName) ||
                                y.appealRevisionCalculations.some((z) => z.taxableValueName)
                            );
                        });
                    });
                },
            },
            {
                field: 'marketValue',
                header: 'MV',
                helpContentId: 'invoice-details.marketValue',
                template: this.numberCellTemplate,
                alignRight: true,
            },
            {
                field: 'cap',
                header: 'CMV',
                helpContentId: 'invoice-details.cap',
                template: this.numberCellTemplate,
                alignRight: true,
                hasValues: (taxSavings) => {
                    return taxSavings.some((x) => x.hasAnyCap);
                },
            },
            {
                field: 'exemption',
                header: 'Exemption MV',
                helpContentId: 'invoice-details.exemptionMV',
                template: this.numberCellTemplate,
                alignRight: true,
                hasValues: (taxSavings) => {
                    return taxSavings.some((x) => x.hasAnyExemption);
                },
            },
            {
                field: 'taxableMarketValue',
                header: 'Taxable MV',
                helpContentId: 'invoice-details.taxableMarketValue',
                template: this.numberCellTemplate,
                alignRight: true,
            },
            {
                field: 'taxableAssessedValue',
                header: 'Taxable AV',
                helpContentId: 'invoice-details.taxableAV',
                template: this.numberCellTemplate,
                alignRight: true,
                hasValues: (taxSavings) => {
                    return taxSavings.some((x) => x.hasAnyTaxableAssessedValue) && taxSavings.some((x) => x.hasAnyRate);
                },
            },
            {
                field: 'baseRate',
                header: 'Base Rate',
                helpContentId: 'invoice-details.baseRate',
                template: this.percentCellTemplate,
                alignRight: true,
                hasValues: (taxSavings) => {
                    return taxSavings.some((x) => x.hasAnyBaseRate) || !taxSavings.some((x) => x.hasAnyRate);
                },
            },
            {
                field: 'rate',
                header: 'Rate',
                helpContentId: 'invoice-details.rate',
                template: this.percentCellTemplate,
                alignRight: true,
                hasValues: (taxSavings) => {
                    return taxSavings.some((x) => x.hasAnyRate);
                },
            },
            {
                field: 'taxAmount',
                header: 'Taxes',
                helpContentId: 'invoice-details.taxes',
                template: this.numberCellTemplate,
                alignRight: true,
                hasDollarSign: true,
            },
        ]);
    }

    includeOtherParcelChange(event: Event): void {
        const includeOtherParcels = (event.target as HTMLInputElement).checked;
        const update = this._invoiceDetailsService.updateIncludeOtherParcel(includeOtherParcels, {
            previousMV: this.invoiceForm().get('previousMV').value,
            previousTAV: this.invoiceForm().get('previousTAV').value,
            invoiceMV: this.invoiceForm().get('invoiceMV').value,
            invoiceTAV: this.invoiceForm().get('invoiceTAV').value,
        });
        this.currentInvoice().includeOtherParcels = includeOtherParcels;
        this.invoiceForm().patchValue(update);

        this.otherParcelsChanged?.emit(includeOtherParcels);
    }
}
